/**
 * 记忆管理页（T13）。
 *
 * 职责：
 * - 列表展示记忆（内容 / 标签 / 重要度 / 召回次数），支持按标签筛选、按重要度或更新时间排序；
 * - 新增 / 编辑（弹窗表单）、删除（二次确认）；
 * - 每个可见部件都通过 `useComponentMeta()` 登记 390x844 逻辑坐标（安卓迁移清单数据源）；
 * - 动效一律引用 `src/design/motion.ts` 预设（pageTransition / tap / listStagger）；
 * - 颜色 / 圆角 / 间距 / 字号 / 阴影全部引用 `src/design/tokens.css`，不硬编码。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { clearDatabase, memoryRepo } from '../../core/repositories';
import { providerRepo } from '../../core/repositories';
import { compareZh } from '../../core/repositories/pagination';
import type { MemoryEntry, MemoryImportance, ProviderConfig } from '../../core/types';
import { useFeatureFlags } from '../../core/featureFlags';
import { SERVER_PROVIDER_ID, providerPurpose } from '../../core/providers';
import { useMotionPresets } from '../../design/motion';
import type { MotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout } from '../../ui';
import {
  IMPORTANCE_LEVELS,
  PAGE_TOP,
  PAGE_BOTTOM,
  BASE_FILTER_TOP,
  FILTER_HEIGHT,
  INJECTION_MODE_HEIGHT,
  INJECTION_MODE_TOP,
  memoryComponentMeta,
  memoryItemBadgeLayout,
  memoryItemContentLayout,
  memoryItemDeleteLayout,
  memoryItemEditLayout,
  memoryItemLayout,
  memoryItemMetaLayout,
  memoryItemTagsLayout,
  memoryLayout,
  memoryMetaInput,
} from './componentMeta';
import { loadOfficialModelOptions } from '../chat/officialModelCatalog';
import type { OfficialModelOption } from '../chat/officialModelCatalog';
import { useProviderModels } from '../chat/useProviderModels';
import {
  FOLLOW_CONVERSATION_PROVIDER,
  loadAutoMemorySettings,
  saveAutoMemorySettings,
} from './autoMemory';
import type { AutoMemorySettings } from './autoMemory';
import {
  MEMORY_INJECTION_CHANGED_EVENT,
  MEMORY_INJECTION_MODE_HINTS,
  MEMORY_INJECTION_MODE_LABELS,
  MEMORY_INJECTION_MODES,
  loadMemoryInjectionMode,
  saveMemoryInjectionMode,
} from './injection';
import type { MemoryInjectionMode } from './injection';
import styles from './MemoryPage.module.css';

const PAGE_TITLE = '记忆';
const NEW_LABEL = '新增记忆';
const EMPTY_TITLE = '还没有记忆';
const EMPTY_HINT = '把重要的事记下来，之后可自动召回进对话。';

/** 画布坐标 → 页面（内容区，原点在 y=24）内绝对定位样式。 */
function boxStyle(
  layout: BoxLayout,
  zIndex: number,
  origin: { x: number; y: number } = { x: 0, y: PAGE_TOP },
): CSSProperties {
  return {
    position: 'absolute',
    left: layout.x - origin.x,
    top: layout.y - origin.y,
    width: layout.width,
    height: layout.height,
    zIndex,
  };
}

/** 子部件相对某父部件的绝对定位（父部件本身使用画布坐标）。 */
function innerBoxStyle(child: BoxLayout, parent: BoxLayout): CSSProperties {
  return {
    position: 'absolute',
    left: child.x - parent.x,
    top: child.y - parent.y,
    width: child.width,
    height: child.height,
  };
}

function collectTags(memories: readonly MemoryEntry[]): string[] {
  const set = new Set<string>();
  for (const memory of memories) {
    for (const tag of memory.tags) set.add(tag);
  }
  return [...set].sort(compareZh);
}

function parseTags(input: string): string[] {
  return input
    .split(/[,，、;；\s]+/)
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

function formatUpdatedAt(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return `今天 ${hh}:${mm}`;
  return `${date.getMonth() + 1}/${date.getDate()} ${hh}:${mm}`;
}

function truncate(text: string, max = 40): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

interface DraftState {
  content: string;
  tags: string;
  importance: MemoryImportance;
}

type EditorState = { mode: 'create' } | { mode: 'edit'; memory: MemoryEntry };

interface MemoryRowProps {
  memory: MemoryEntry;
  index: number;
  listTop: number;
  presets: MotionPresets;
  onEdit: (memory: MemoryEntry) => void;
  onRequestDelete: (memory: MemoryEntry) => void;
}

/** 单条记忆卡片：内容 / 重要度 / 标签 / 召回次数 + 编辑、删除按钮。 */
function MemoryRow({
  memory,
  index,
  listTop,
  presets,
  onEdit,
  onRequestDelete,
}: MemoryRowProps): ReactElement {
  const item = memoryItemLayout(index, listTop);
  const baseId = `memory.item.${memory.id}`;

  const rowRef = useComponentMeta<HTMLDivElement>(
    buildComponentMeta(
      memoryMetaInput(baseId, `记忆：${memory.content}`, 'container', item, {
        zIndex: 4,
        props: ['content', 'tags', 'importance', 'recallCount'],
        notes: '记忆卡片实例（模板坐标见 memory.item）',
      }),
    ),
  );
  const contentRef = useComponentMeta<HTMLSpanElement>(
    buildComponentMeta(
      memoryMetaInput(`${baseId}.content`, '记忆内容', 'text', memoryItemContentLayout(item), {
        zIndex: 5,
        notes: '单行省略号',
      }),
    ),
  );
  const badgeRef = useComponentMeta<HTMLSpanElement>(
    buildComponentMeta(
      memoryMetaInput(`${baseId}.badge`, `重要度 ${memory.importance}`, 'text', memoryItemBadgeLayout(item), {
        zIndex: 5,
      }),
    ),
  );
  const tagsRef = useComponentMeta<HTMLSpanElement>(
    buildComponentMeta(
      memoryMetaInput(`${baseId}.tags`, '记忆标签', 'text', memoryItemTagsLayout(item), { zIndex: 5 }),
    ),
  );
  const metaRef = useComponentMeta<HTMLSpanElement>(
    buildComponentMeta(
      memoryMetaInput(`${baseId}.meta`, '召回次数与更新时间', 'text', memoryItemMetaLayout(item), {
        zIndex: 5,
      }),
    ),
  );
  const editRef = useComponentMeta<HTMLButtonElement>(
    buildComponentMeta(
      memoryMetaInput(`${baseId}.editButton`, '编辑记忆', 'button', memoryItemEditLayout(item), {
        zIndex: 6,
      }),
    ),
  );
  const deleteRef = useComponentMeta<HTMLButtonElement>(
    buildComponentMeta(
      memoryMetaInput(`${baseId}.deleteButton`, '删除记忆', 'button', memoryItemDeleteLayout(item), {
        zIndex: 6,
        notes: '点击后二次确认',
      }),
    ),
  );

  const tagsText = memory.tags.length > 0 ? memory.tags.map((tag) => `#${tag}`).join(' ') : '（无标签）';

  return (
    <li className={styles.rowWrap} style={{ height: item.height }}>
      <motion.div
        ref={rowRef}
        className={styles.row}
        variants={presets.listStagger.item}
        data-ui-id={baseId}
        data-ui-role="container"
      >
        <span
          ref={contentRef}
          className={styles.rowContent}
          style={innerBoxStyle(memoryItemContentLayout(item), item)}
          data-ui-id={`${baseId}.content`}
        >
          {memory.content}
        </span>
        <span
          ref={badgeRef}
          className={styles.rowBadge}
          style={innerBoxStyle(memoryItemBadgeLayout(item), item)}
          data-ui-id={`${baseId}.badge`}
        >
          {`重要度 ${memory.importance}`}
        </span>
        <span
          ref={tagsRef}
          className={styles.rowTags}
          style={innerBoxStyle(memoryItemTagsLayout(item), item)}
          data-ui-id={`${baseId}.tags`}
        >
          {tagsText}
        </span>
        <span
          ref={metaRef}
          className={styles.rowMeta}
          style={innerBoxStyle(memoryItemMetaLayout(item), item)}
          data-ui-id={`${baseId}.meta`}
        >
          {`召回 ${memory.recallCount} 次 · ${formatUpdatedAt(memory.updatedAt)}`}
        </span>
        <motion.button
          ref={editRef}
          type="button"
          className={styles.rowAction}
          style={innerBoxStyle(memoryItemEditLayout(item), item)}
          data-ui-id={`${baseId}.editButton`}
          aria-label={`编辑记忆：${memory.content}`}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onEdit(memory)}
        >
          改
        </motion.button>
        <motion.button
          ref={deleteRef}
          type="button"
          className={`${styles.rowAction} ${styles.rowActionDanger}`}
          style={innerBoxStyle(memoryItemDeleteLayout(item), item)}
          data-ui-id={`${baseId}.deleteButton`}
          aria-label={`删除记忆：${memory.content}`}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onRequestDelete(memory)}
        >
          删
        </motion.button>
      </motion.div>
    </li>
  );
}

interface AutoMemoryModelDialogProps {
  current: AutoMemorySettings;
  providers: readonly ProviderConfig[];
  officialOptions: readonly OfficialModelOption[];
  presets: MotionPresets;
  onCancel: () => void;
  onSave: (settings: AutoMemorySettings) => void;
}

/** 自动记忆的提供商 / 模型选择：跟随对话、官方模型或任意自定义提供商。 */
function AutoMemoryModelDialog({
  current,
  providers,
  officialOptions,
  presets,
  onCancel,
  onSave,
}: AutoMemoryModelDialogProps): ReactElement {
  const [providerId, setProviderId] = useState(current.providerId);
  const [model, setModel] = useState(current.model);
  const provider =
    providerId !== FOLLOW_CONVERSATION_PROVIDER && providerId !== SERVER_PROVIDER_ID
      ? providers.find((item) => item.id === providerId) ?? null
      : null;
  const catalog = useProviderModels(provider);

  const models = useMemo(() => {
    if (providerId === SERVER_PROVIDER_ID) return officialOptions.map((option) => option.slug);
    if (provider === null) return [];
    return [...new Set([provider.defaultModel ?? '', ...catalog.models].map((item) => item.trim()).filter(Boolean))];
  }, [catalog.models, officialOptions, provider, providerId]);

  useEffect(() => {
    if (providerId === FOLLOW_CONVERSATION_PROVIDER || models.length === 0) return;
    if (!models.includes(model)) setModel(models[0] ?? '');
  }, [model, models, providerId]);

  const dialogLayout = memoryLayout('memory.autoMemory.picker.dialog');
  const backdropRef = useComponentMeta<HTMLDivElement>(
    memoryComponentMeta('memory.autoMemory.picker.backdrop'),
  );
  const dialogRef = useComponentMeta<HTMLDivElement>(
    memoryComponentMeta('memory.autoMemory.picker.dialog'),
  );
  const titleRef = useComponentMeta<HTMLHeadingElement>(
    memoryComponentMeta('memory.autoMemory.picker.title'),
  );
  const providerLabelRef = useComponentMeta<HTMLLabelElement>(
    memoryComponentMeta('memory.autoMemory.picker.providerLabel'),
  );
  const providerSelectRef = useComponentMeta<HTMLSelectElement>(
    memoryComponentMeta('memory.autoMemory.picker.providerSelect'),
  );
  const modelLabelRef = useComponentMeta<HTMLLabelElement>(
    memoryComponentMeta('memory.autoMemory.picker.modelLabel'),
  );
  const modelSelectRef = useComponentMeta<HTMLSelectElement>(
    memoryComponentMeta('memory.autoMemory.picker.modelSelect'),
  );
  const hintRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.autoMemory.picker.hint'),
  );
  const cancelRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.autoMemory.picker.cancelButton'),
  );
  const saveRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.autoMemory.picker.saveButton'),
  );

  const chooseProvider = (next: string): void => {
    setProviderId(next);
    if (next === FOLLOW_CONVERSATION_PROVIDER) {
      setModel('');
    } else if (next === SERVER_PROVIDER_ID) {
      setModel(officialOptions[0]?.slug ?? '');
    } else {
      setModel(providers.find((item) => item.id === next)?.defaultModel ?? '');
    }
  };

  const hint =
    providerId === FOLLOW_CONVERSATION_PROVIDER
      ? '使用当前对话所选的提供商和模型；切换对话时会跟着变化。'
      : providerId === SERVER_PROVIDER_ID
        ? catalog.loading
          ? '正在加载官方模型…'
          : officialOptions.length === 0
            ? '暂无可用官方模型，请先登录或让管理员发布模型。'
            : '使用官方模型整理记忆，会按 token 计入账户消耗。'
        : catalog.loading
          ? '正在读取该提供商的模型列表…'
          : catalog.error ?? (models.length === 0 ? '该提供商暂无可用模型，请先配置默认模型或刷新。' : '使用该提供商整理记忆，费用由提供商账户结算。');

  return (
    <>
      <motion.div
        ref={backdropRef}
        className={styles.backdrop}
        style={boxStyle(memoryLayout('memory.autoMemory.picker.backdrop'), 50)}
        variants={presets.sheetBackdrop}
        initial="hidden"
        animate="visible"
        onClick={onCancel}
        data-ui-id="memory.autoMemory.picker.backdrop"
        data-ui-role="background"
      />
      <motion.div
        ref={dialogRef}
        className={styles.dialog}
        style={boxStyle(dialogLayout, 51)}
        variants={presets.sheet}
        initial="hidden"
        animate="visible"
        role="dialog"
        aria-modal="true"
        aria-label="自动记忆模型"
        data-ui-id="memory.autoMemory.picker.dialog"
        data-ui-role="modal"
      >
        <h2
          ref={titleRef}
          className={styles.dialogTitle}
          style={innerBoxStyle(memoryLayout('memory.autoMemory.picker.title'), dialogLayout)}
          data-ui-id="memory.autoMemory.picker.title"
        >
          自动记忆模型
        </h2>
        <label
          ref={providerLabelRef}
          className={styles.fieldLabel}
          style={innerBoxStyle(memoryLayout('memory.autoMemory.picker.providerLabel'), dialogLayout)}
          htmlFor="auto-memory-provider"
          data-ui-id="memory.autoMemory.picker.providerLabel"
        >
          提供商
        </label>
        <select
          id="auto-memory-provider"
          ref={providerSelectRef}
          className={styles.select}
          style={innerBoxStyle(memoryLayout('memory.autoMemory.picker.providerSelect'), dialogLayout)}
          value={providerId}
          aria-label="自动记忆提供商"
          onChange={(event) => chooseProvider(event.currentTarget.value)}
          data-ui-id="memory.autoMemory.picker.providerSelect"
        >
          <option value={FOLLOW_CONVERSATION_PROVIDER}>跟随对话模型</option>
          <option value={SERVER_PROVIDER_ID}>官方模型</option>
          {providers.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <label
          ref={modelLabelRef}
          className={styles.fieldLabel}
          style={innerBoxStyle(memoryLayout('memory.autoMemory.picker.modelLabel'), dialogLayout)}
          htmlFor="auto-memory-model"
          data-ui-id="memory.autoMemory.picker.modelLabel"
        >
          模型
        </label>
        <select
          id="auto-memory-model"
          ref={modelSelectRef}
          className={styles.select}
          style={innerBoxStyle(memoryLayout('memory.autoMemory.picker.modelSelect'), dialogLayout)}
          value={model}
          aria-label="自动记忆模型"
          disabled={providerId === FOLLOW_CONVERSATION_PROVIDER || (catalog.loading && models.length === 0)}
          onChange={(event) => setModel(event.currentTarget.value)}
          data-ui-id="memory.autoMemory.picker.modelSelect"
        >
          {providerId === FOLLOW_CONVERSATION_PROVIDER ? (
            <option value="">当前会话选择的模型</option>
          ) : models.length === 0 ? (
            <option value="">{catalog.loading ? '正在加载…' : '暂无可用模型'}</option>
          ) : (
            models.map((item) => {
              const official = officialOptions.find((option) => option.slug === item);
              return (
                <option key={item} value={item}>
                  {official ? `${official.displayName} (${item})` : item}
                </option>
              );
            })
          )}
        </select>
        <p
          ref={hintRef}
          className={styles.dialogMessage}
          style={innerBoxStyle(memoryLayout('memory.autoMemory.picker.hint'), dialogLayout)}
          data-ui-id="memory.autoMemory.picker.hint"
        >
          {hint}
        </p>
        <motion.button
          ref={cancelRef}
          type="button"
          className={styles.secondaryButton}
          style={innerBoxStyle(memoryLayout('memory.autoMemory.picker.cancelButton'), dialogLayout)}
          data-ui-id="memory.autoMemory.picker.cancelButton"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onCancel}
        >
          取消
        </motion.button>
        <motion.button
          ref={saveRef}
          type="button"
          className={styles.primaryButton}
          style={innerBoxStyle(memoryLayout('memory.autoMemory.picker.saveButton'), dialogLayout)}
          data-ui-id="memory.autoMemory.picker.saveButton"
          disabled={providerId !== FOLLOW_CONVERSATION_PROVIDER && model.trim() === ''}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onSave({ enabled: current.enabled, providerId, model: providerId === FOLLOW_CONVERSATION_PROVIDER ? '' : model })}
        >
          保存
        </motion.button>
      </motion.div>
    </>
  );
}

interface MemoryEditorDialogProps {
  mode: EditorState['mode'];
  initial: MemoryEntry | null;
  presets: MotionPresets;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (draft: DraftState) => void;
}

/** 新增 / 编辑记忆弹窗。 */
function MemoryEditorDialog({
  mode,
  initial,
  presets,
  busy,
  onCancel,
  onSubmit,
}: MemoryEditorDialogProps): ReactElement {
  const [content, setContent] = useState(initial?.content ?? '');
  const [tags, setTags] = useState(initial ? initial.tags.join(', ') : '');
  const [importance, setImportance] = useState<MemoryImportance>(initial?.importance ?? 3);

  const title = mode === 'edit' ? '编辑记忆' : '新增记忆';
  const dialogLayout = memoryLayout('memory.editor.dialog');

  const backdropRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.editor.backdrop'));
  const dialogRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.editor.dialog'));
  const titleRef = useComponentMeta<HTMLHeadingElement>(memoryComponentMeta('memory.editor.title'));
  const contentLabelRef = useComponentMeta<HTMLLabelElement>(
    memoryComponentMeta('memory.editor.contentLabel'),
  );
  const contentInputRef = useComponentMeta<HTMLTextAreaElement>(
    memoryComponentMeta('memory.editor.contentInput'),
  );
  const tagsLabelRef = useComponentMeta<HTMLLabelElement>(memoryComponentMeta('memory.editor.tagsLabel'));
  const tagsInputRef = useComponentMeta<HTMLInputElement>(memoryComponentMeta('memory.editor.tagsInput'));
  const importanceLabelRef = useComponentMeta<HTMLLabelElement>(
    memoryComponentMeta('memory.editor.importanceLabel'),
  );
  const importanceSelectRef = useComponentMeta<HTMLSelectElement>(
    memoryComponentMeta('memory.editor.importanceSelect'),
  );
  const saveRef = useComponentMeta<HTMLButtonElement>(memoryComponentMeta('memory.editor.saveButton'));
  const cancelRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.editor.cancelButton'),
  );

  return (
    <>
      <motion.div
        ref={backdropRef}
        className={styles.backdrop}
        style={boxStyle(memoryLayout('memory.editor.backdrop'), 20)}
        variants={presets.sheetBackdrop}
        initial="hidden"
        animate="visible"
        onClick={onCancel}
        data-ui-id="memory.editor.backdrop"
        data-ui-role="background"
      />
      <motion.div
        ref={dialogRef}
        className={styles.dialog}
        style={boxStyle(dialogLayout, 21)}
        variants={presets.sheet}
        initial="hidden"
        animate="visible"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-ui-id="memory.editor.dialog"
        data-ui-role="modal"
      >
        <h2
          ref={titleRef}
          className={styles.dialogTitle}
          style={innerBoxStyle(memoryLayout('memory.editor.title'), dialogLayout)}
          data-ui-id="memory.editor.title"
        >
          {title}
        </h2>
        <label
          ref={contentLabelRef}
          className={styles.fieldLabel}
          style={innerBoxStyle(memoryLayout('memory.editor.contentLabel'), dialogLayout)}
          htmlFor="memory-editor-content"
          data-ui-id="memory.editor.contentLabel"
        >
          内容
        </label>
        <textarea
          id="memory-editor-content"
          ref={contentInputRef}
          className={styles.textarea}
          style={innerBoxStyle(memoryLayout('memory.editor.contentInput'), dialogLayout)}
          value={content}
          placeholder="要记住的内容"
          aria-label="记忆内容"
          onChange={(event) => setContent(event.target.value)}
          data-ui-id="memory.editor.contentInput"
        />
        <label
          ref={tagsLabelRef}
          className={styles.fieldLabel}
          style={innerBoxStyle(memoryLayout('memory.editor.tagsLabel'), dialogLayout)}
          htmlFor="memory-editor-tags"
          data-ui-id="memory.editor.tagsLabel"
        >
          标签（逗号分隔）
        </label>
        <input
          id="memory-editor-tags"
          ref={tagsInputRef}
          className={styles.input}
          style={innerBoxStyle(memoryLayout('memory.editor.tagsInput'), dialogLayout)}
          value={tags}
          placeholder="例如：偏好, 工作"
          aria-label="记忆标签"
          onChange={(event) => setTags(event.target.value)}
          data-ui-id="memory.editor.tagsInput"
        />
        <label
          ref={importanceLabelRef}
          className={styles.fieldLabel}
          style={innerBoxStyle(memoryLayout('memory.editor.importanceLabel'), dialogLayout)}
          htmlFor="memory-editor-importance"
          data-ui-id="memory.editor.importanceLabel"
        >
          重要度（1..5）
        </label>
        <select
          id="memory-editor-importance"
          ref={importanceSelectRef}
          className={styles.select}
          style={innerBoxStyle(memoryLayout('memory.editor.importanceSelect'), dialogLayout)}
          value={importance}
          aria-label="重要度"
          onChange={(event) => setImportance(memoryRepo.normalizeImportance(Number(event.target.value)))}
          data-ui-id="memory.editor.importanceSelect"
        >
          {IMPORTANCE_LEVELS.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>
        <motion.button
          ref={saveRef}
          type="button"
          className={styles.primaryButton}
          style={innerBoxStyle(memoryLayout('memory.editor.saveButton'), dialogLayout)}
          data-ui-id="memory.editor.saveButton"
          disabled={busy || content.trim() === ''}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onSubmit({ content: content.trim(), tags, importance })}
        >
          保存
        </motion.button>
        <motion.button
          ref={cancelRef}
          type="button"
          className={styles.secondaryButton}
          style={innerBoxStyle(memoryLayout('memory.editor.cancelButton'), dialogLayout)}
          data-ui-id="memory.editor.cancelButton"
          disabled={busy}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onCancel}
        >
          取消
        </motion.button>
      </motion.div>
    </>
  );
}

interface DeleteConfirmDialogProps {
  memory: MemoryEntry;
  presets: MotionPresets;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** 删除二次确认弹窗。 */
function DeleteConfirmDialog({
  memory,
  presets,
  busy,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps): ReactElement {
  const dialogLayout = memoryLayout('memory.confirm.dialog');
  const backdropRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.confirm.backdrop'));
  const dialogRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.confirm.dialog'));
  const titleRef = useComponentMeta<HTMLParagraphElement>(memoryComponentMeta('memory.confirm.title'));
  const messageRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.confirm.message'),
  );
  const cancelRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.confirm.cancelButton'),
  );
  const confirmRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.confirm.confirmButton'),
  );

  return (
    <>
      <motion.div
        ref={backdropRef}
        className={styles.backdrop}
        style={boxStyle(memoryLayout('memory.confirm.backdrop'), 30)}
        variants={presets.sheetBackdrop}
        initial="hidden"
        animate="visible"
        onClick={onCancel}
        data-ui-id="memory.confirm.backdrop"
        data-ui-role="background"
      />
      <motion.div
        ref={dialogRef}
        className={styles.dialog}
        style={boxStyle(dialogLayout, 31)}
        variants={presets.sheet}
        initial="hidden"
        animate="visible"
        role="dialog"
        aria-modal="true"
        aria-label="删除记忆确认"
        data-ui-id="memory.confirm.dialog"
        data-ui-role="modal"
      >
        <p
          ref={titleRef}
          className={styles.dialogTitle}
          style={innerBoxStyle(memoryLayout('memory.confirm.title'), dialogLayout)}
          data-ui-id="memory.confirm.title"
        >
          删除这条记忆？
        </p>
        <p
          ref={messageRef}
          className={styles.dialogMessage}
          style={innerBoxStyle(memoryLayout('memory.confirm.message'), dialogLayout)}
          data-ui-id="memory.confirm.message"
        >
          {`「${truncate(memory.content)}」删除后不可恢复。`}
        </p>
        <motion.button
          ref={cancelRef}
          type="button"
          className={styles.secondaryButton}
          style={innerBoxStyle(memoryLayout('memory.confirm.cancelButton'), dialogLayout)}
          data-ui-id="memory.confirm.cancelButton"
          aria-label="取消删除"
          disabled={busy}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onCancel}
        >
          取消
        </motion.button>
        <motion.button
          ref={confirmRef}
          type="button"
          className={styles.dangerButton}
          style={innerBoxStyle(memoryLayout('memory.confirm.confirmButton'), dialogLayout)}
          data-ui-id="memory.confirm.confirmButton"
          aria-label="确认删除"
          disabled={busy}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onConfirm}
        >
          确认删除
        </motion.button>
      </motion.div>
    </>
  );
}

interface ResetConfirmDialogProps {
  presets: MotionPresets;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** 重置数据二次确认弹窗：确认后调用 clearDatabase() 清空全部本地数据。 */
function ResetConfirmDialog({
  presets,
  busy,
  onCancel,
  onConfirm,
}: ResetConfirmDialogProps): ReactElement {
  const dialogLayout = memoryLayout('memory.resetConfirm.dialog');
  const backdropRef = useComponentMeta<HTMLDivElement>(
    memoryComponentMeta('memory.resetConfirm.backdrop'),
  );
  const dialogRef = useComponentMeta<HTMLDivElement>(
    memoryComponentMeta('memory.resetConfirm.dialog'),
  );
  const titleRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.resetConfirm.title'),
  );
  const messageRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.resetConfirm.message'),
  );
  const cancelRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.resetConfirm.cancelButton'),
  );
  const confirmRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.resetConfirm.confirmButton'),
  );

  return (
    <>
      <motion.div
        ref={backdropRef}
        className={styles.backdrop}
        style={boxStyle(memoryLayout('memory.resetConfirm.backdrop'), 40)}
        variants={presets.sheetBackdrop}
        initial="hidden"
        animate="visible"
        onClick={onCancel}
        data-ui-id="memory.resetConfirm.backdrop"
        data-ui-role="background"
      />
      <motion.div
        ref={dialogRef}
        className={styles.dialog}
        style={boxStyle(dialogLayout, 41)}
        variants={presets.sheet}
        initial="hidden"
        animate="visible"
        role="dialog"
        aria-modal="true"
        aria-label="重置数据确认"
        data-ui-id="memory.resetConfirm.dialog"
        data-ui-role="modal"
      >
        <p
          ref={titleRef}
          className={styles.dialogTitle}
          style={innerBoxStyle(memoryLayout('memory.resetConfirm.title'), dialogLayout)}
          data-ui-id="memory.resetConfirm.title"
        >
          重置全部本地数据？
        </p>
        <p
          ref={messageRef}
          className={styles.dialogMessage}
          style={innerBoxStyle(memoryLayout('memory.resetConfirm.message'), dialogLayout)}
          data-ui-id="memory.resetConfirm.message"
        >
          将清空对话、消息、角色卡、提供商与记忆，且不可恢复。
        </p>
        <motion.button
          ref={cancelRef}
          type="button"
          className={styles.secondaryButton}
          style={innerBoxStyle(memoryLayout('memory.resetConfirm.cancelButton'), dialogLayout)}
          data-ui-id="memory.resetConfirm.cancelButton"
          aria-label="取消重置"
          disabled={busy}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onCancel}
        >
          取消
        </motion.button>
        <motion.button
          ref={confirmRef}
          type="button"
          className={styles.dangerButton}
          style={innerBoxStyle(memoryLayout('memory.resetConfirm.confirmButton'), dialogLayout)}
          data-ui-id="memory.resetConfirm.confirmButton"
          aria-label="确认重置"
          disabled={busy}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onConfirm}
        >
          确认重置
        </motion.button>
      </motion.div>
    </>
  );
}

/** 记忆管理页。 */
export function MemoryPage(): ReactElement {
  const presets = useMotionPresets();
  const flags = useFeatureFlags();
  const autoMemoryVisible = flags.autoMemory;
  const [memories, setMemories] = useState<MemoryEntry[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'importance' | 'updatedAt'>('updatedAt');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MemoryEntry | null>(null);
  const [pendingReset, setPendingReset] = useState(false);
  const [autoMemory, setAutoMemory] = useState<AutoMemorySettings>(() => loadAutoMemorySettings());
  const [autoMemoryPickerOpen, setAutoMemoryPickerOpen] = useState(false);
  const [injectionMode, setInjectionMode] = useState<MemoryInjectionMode>(() =>
    loadMemoryInjectionMode(),
  );
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [officialOptions, setOfficialOptions] = useState<OfficialModelOption[]>([]);

  const injectionTop = autoMemoryVisible ? INJECTION_MODE_TOP : BASE_FILTER_TOP;
  const filterTop = injectionTop + INJECTION_MODE_HEIGHT;
  const listTop = filterTop + FILTER_HEIGHT;
  const filterLayout = { x: 0, y: filterTop, width: 390, height: FILTER_HEIGHT };
  const listLayout = { x: 0, y: listTop, width: 390, height: PAGE_BOTTOM - listTop };
  const offset = listTop - (INJECTION_MODE_TOP + INJECTION_MODE_HEIGHT + FILTER_HEIGHT);

  const rootRef = useComponentMeta<HTMLElement>(memoryComponentMeta('page.memory.root'));
  const headerRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.header'));
  const headerTitleRef = useComponentMeta<HTMLHeadingElement>(
    memoryComponentMeta('memory.header.title'),
  );
  const newButtonRef = useComponentMeta<HTMLButtonElement>(memoryComponentMeta('memory.newButton'));
  const resetButtonRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.resetButton'),
  );
  const autoMemoryBarRef = useComponentMeta<HTMLDivElement>(
    memoryComponentMeta('memory.autoMemory.bar'),
  );
  const autoMemoryToggleRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.autoMemory.toggle'),
  );
  const autoMemoryModelRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.autoMemory.modelButton'),
  );
  const autoMemoryHintRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.autoMemory.hint'),
  );
  const injectionBarRef = useComponentMeta<HTMLDivElement>(
    memoryComponentMeta('memory.injectionMode.bar'),
  );
  const injectionLabelRef = useComponentMeta<HTMLLabelElement>(
    memoryComponentMeta('memory.injectionMode.label'),
  );
  const injectionSelectRef = useComponentMeta<HTMLSelectElement>(
    memoryComponentMeta('memory.injectionMode.select'),
  );
  const injectionHintRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.injectionMode.hint'),
  );
  const filterBarRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.filterBar'));
  const filterTagsRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.filter.tags'));
  const sortButtonRef = useComponentMeta<HTMLButtonElement>(
    memoryComponentMeta('memory.filter.sortButton'),
  );
  const listRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.list'));
  const emptyRef = useComponentMeta<HTMLDivElement>(memoryComponentMeta('memory.empty'));
  const emptyTitleRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.empty.title'),
  );
  const emptyHintRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.empty.hint'),
  );
  const errorBannerRef = useComponentMeta<HTMLDivElement>(
    memoryComponentMeta('memory.errorBanner'),
  );
  const errorTextRef = useComponentMeta<HTMLParagraphElement>(
    memoryComponentMeta('memory.errorBanner.message'),
  );

  const reload = useCallback(async (): Promise<void> => {
    try {
      const [visible, all] = await Promise.all([
        memoryRepo.listMemories({ tag: selectedTag ?? undefined, sortBy, order: 'desc' }),
        memoryRepo.listMemories({ sortBy, order: 'desc' }),
      ]);
      setMemories(visible);
      setTagOptions(collectTags(all));
      setError(null);
    } catch {
      setError('读取本地记忆失败');
    } finally {
      setLoading(false);
    }
  }, [selectedTag, sortBy]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!autoMemoryVisible) return;
    let alive = true;
    void (async () => {
      try {
        const list = await providerRepo.listProviders({ enabled: true });
        if (alive) setProviders(list.filter((provider) => providerPurpose(provider) === 'chat'));
      } catch {
        if (alive) setProviders([]);
      }
      try {
        const options = await loadOfficialModelOptions();
        if (alive) setOfficialOptions(options);
      } catch {
        if (alive) setOfficialOptions([]);
      }
    })();
    const sync = (): void => setAutoMemory(loadAutoMemorySettings());
    window.addEventListener('storage', sync);
    window.addEventListener('zxtalk:auto-memory-changed', sync);
    return () => {
      alive = false;
      window.removeEventListener('storage', sync);
      window.removeEventListener('zxtalk:auto-memory-changed', sync);
    };
  }, [autoMemoryVisible]);

  useEffect(() => {
    const sync = (): void => setInjectionMode(loadMemoryInjectionMode());
    window.addEventListener('storage', sync);
    window.addEventListener(MEMORY_INJECTION_CHANGED_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(MEMORY_INJECTION_CHANGED_EVENT, sync);
    };
  }, []);

  const submitEditor = useCallback(
    async (draft: DraftState): Promise<void> => {
      setBusy(true);
      try {
        const payload = {
          content: draft.content,
          tags: parseTags(draft.tags),
          importance: draft.importance,
        };
        if (editor?.mode === 'edit') {
          await memoryRepo.updateMemory(editor.memory.id, payload);
        } else {
          await memoryRepo.createAndSaveMemory(payload);
        }
        setEditor(null);
        await reload();
      } catch {
        setError('保存记忆失败');
      } finally {
        setBusy(false);
      }
    },
    [editor, reload],
  );

  const confirmDelete = useCallback(async (): Promise<void> => {
    const target = pendingDelete;
    if (target === null) return;
    setBusy(true);
    try {
      await memoryRepo.deleteMemory(target.id);
      setPendingDelete(null);
      await reload();
    } catch {
      setError('删除记忆失败');
    } finally {
      setBusy(false);
    }
  }, [pendingDelete, reload]);

  /** 清空全部本地数据（对话 / 消息 / 角色卡 / 提供商 / 记忆），与全局「重置数据」走同一入口。 */
  const confirmReset = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      await clearDatabase();
      setPendingReset(false);
      setSelectedTag(null);
      await reload();
    } catch {
      setError('重置数据失败');
    } finally {
      setBusy(false);
    }
  }, [reload]);

  const toggleSort = useCallback((): void => {
    setSortBy((previous) => (previous === 'importance' ? 'updatedAt' : 'importance'));
  }, []);

  const sortLabel = sortBy === 'importance' ? '按重要度' : '按时间';
  const autoMemoryProvider =
    autoMemory.providerId === FOLLOW_CONVERSATION_PROVIDER ||
    autoMemory.providerId === SERVER_PROVIDER_ID
      ? null
      : providers.find((item) => item.id === autoMemory.providerId) ?? null;
  const autoMemoryModelLabel =
    autoMemory.providerId === FOLLOW_CONVERSATION_PROVIDER
      ? '模型 · 跟随对话'
      : autoMemory.providerId === SERVER_PROVIDER_ID
        ? `模型 · 官方 · ${
            officialOptions.find((option) => option.slug === autoMemory.model)?.displayName ??
            autoMemory.model ??
            '未选择'
          }`
        : `模型 · ${autoMemoryProvider?.name ?? '已失效提供商'} · ${
            autoMemory.model.trim() !== ''
              ? autoMemory.model
              : autoMemoryProvider?.defaultModel ?? '未选择'
          }`;

  return (
    <section
      ref={rootRef}
      className={styles.page}
      data-ui-id="page.memory.root"
      data-ui-role="container"
      data-skin-surface="page"
    >
      <motion.div
        className={styles.enter}
        variants={presets.pageTransition}
        custom="forward"
        initial="enter"
        animate="center"
      >
        <div
          ref={headerRef}
          className={styles.header}
          style={boxStyle(memoryLayout('memory.header'), 3)}
          data-ui-id="memory.header"
          data-ui-role="container"
        >
          <h1
            ref={headerTitleRef}
            className={styles.title}
            style={boxStyle(memoryLayout('memory.header.title'), 4)}
            data-ui-id="memory.header.title"
          >
            {PAGE_TITLE}
          </h1>
          <motion.button
            ref={newButtonRef}
            type="button"
            className={styles.newButton}
            style={boxStyle(memoryLayout('memory.newButton'), 4)}
            data-ui-id="memory.newButton"
            aria-label={NEW_LABEL}
            whileTap={presets.tap.whileTap}
            transition={presets.tap.transition}
            onClick={() => setEditor({ mode: 'create' })}
          >
            ＋
          </motion.button>
          <motion.button
            ref={resetButtonRef}
            type="button"
            className={styles.resetButton}
            style={boxStyle(memoryLayout('memory.resetButton'), 4)}
            data-ui-id="memory.resetButton"
            aria-label="重置数据"
            disabled={busy}
            whileTap={presets.tap.whileTap}
            transition={presets.tap.transition}
            onClick={() => setPendingReset(true)}
          >
            重置
          </motion.button>
        </div>

        {autoMemoryVisible ? (
          <div
            ref={autoMemoryBarRef}
            className={styles.autoMemoryBar}
            style={boxStyle(memoryLayout('memory.autoMemory.bar'), 3)}
            data-ui-id="memory.autoMemory.bar"
            data-ui-role="container"
          >
            <motion.button
              ref={autoMemoryToggleRef}
              type="button"
              role="switch"
              aria-checked={autoMemory.enabled}
              aria-label="自动记忆"
              className={
                autoMemory.enabled
                  ? `${styles.autoMemoryToggle} ${styles.autoMemoryToggleOn}`
                  : styles.autoMemoryToggle
              }
              style={innerBoxStyle(
                memoryLayout('memory.autoMemory.toggle'),
                memoryLayout('memory.autoMemory.bar'),
              )}
              data-ui-id="memory.autoMemory.toggle"
              whileTap={presets.tap.whileTap}
              transition={presets.tap.transition}
              onClick={() => {
                setAutoMemory(
                  saveAutoMemorySettings({ ...autoMemory, enabled: !autoMemory.enabled }),
                );
              }}
            >
              <span>自动记忆</span>
              <span
                className={
                  autoMemory.enabled
                    ? `${styles.switchTrack} ${styles.switchTrackOn}`
                    : styles.switchTrack
                }
                aria-hidden="true"
              >
                <span className={styles.switchKnob} />
              </span>
            </motion.button>
            <motion.button
              ref={autoMemoryModelRef}
              type="button"
              className={styles.autoMemoryModel}
              style={innerBoxStyle(
                memoryLayout('memory.autoMemory.modelButton'),
                memoryLayout('memory.autoMemory.bar'),
              )}
              data-ui-id="memory.autoMemory.modelButton"
              aria-label="自动记忆模型"
              whileTap={presets.tap.whileTap}
              transition={presets.tap.transition}
              onClick={() => setAutoMemoryPickerOpen(true)}
            >
              {autoMemoryModelLabel}
            </motion.button>
            <p
              ref={autoMemoryHintRef}
              className={styles.autoMemoryHint}
              style={innerBoxStyle(
                memoryLayout('memory.autoMemory.hint'),
                memoryLayout('memory.autoMemory.bar'),
              )}
              data-ui-id="memory.autoMemory.hint"
            >
              开启后栀歆将会越来越懂你，但是消耗也会越来越大。
              <br />
              （数据仅本地保存）
            </p>
          </div>
        ) : null}

        <div
          ref={injectionBarRef}
          className={styles.injectionModeBar}
          style={boxStyle({ ...memoryLayout('memory.injectionMode.bar'), y: injectionTop }, 3)}
          data-ui-id="memory.injectionMode.bar"
          data-ui-role="container"
        >
          <label
            ref={injectionLabelRef}
            className={styles.injectionModeLabel}
            htmlFor="memory-injection-mode"
            style={innerBoxStyle(
              { ...memoryLayout('memory.injectionMode.label'), y: injectionTop + 8 },
              { ...memoryLayout('memory.injectionMode.bar'), y: injectionTop },
            )}
            data-ui-id="memory.injectionMode.label"
          >
            注入模式
          </label>
          <select
            id="memory-injection-mode"
            ref={injectionSelectRef}
            className={styles.injectionModeSelect}
            style={innerBoxStyle(
              { ...memoryLayout('memory.injectionMode.select'), y: injectionTop + 8 },
              { ...memoryLayout('memory.injectionMode.bar'), y: injectionTop },
            )}
            value={injectionMode}
            aria-label="注入模式"
            onChange={(event) => {
              setInjectionMode(
                saveMemoryInjectionMode(event.currentTarget.value as MemoryInjectionMode),
              );
            }}
            data-ui-id="memory.injectionMode.select"
          >
            {MEMORY_INJECTION_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {MEMORY_INJECTION_MODE_LABELS[mode]}
              </option>
            ))}
          </select>
          <p
            ref={injectionHintRef}
            className={styles.injectionModeHint}
            style={innerBoxStyle(
              { ...memoryLayout('memory.injectionMode.hint'), y: injectionTop + 54 },
              { ...memoryLayout('memory.injectionMode.bar'), y: injectionTop },
            )}
            data-ui-id="memory.injectionMode.hint"
          >
            {MEMORY_INJECTION_MODE_HINTS[injectionMode]}
          </p>
        </div>

        <div
          ref={filterBarRef}
          className={styles.filterBar}
          style={boxStyle(filterLayout, 3)}
          data-ui-id="memory.filterBar"
          data-ui-role="container"
        >
          <div
            ref={filterTagsRef}
            className={styles.tagList}
            style={innerBoxStyle(
              { ...memoryLayout('memory.filter.tags'), y: filterTop + 10 },
              filterLayout,
            )}
            data-ui-id="memory.filter.tags"
            data-ui-role="list"
          >
            <button
              type="button"
              className={selectedTag === null ? `${styles.tagChip} ${styles.tagChipActive}` : styles.tagChip}
              aria-pressed={selectedTag === null}
              onClick={() => setSelectedTag(null)}
            >
              全部
            </button>
            {tagOptions.map((tag) => (
              <button
                key={tag}
                type="button"
                className={selectedTag === tag ? `${styles.tagChip} ${styles.tagChipActive}` : styles.tagChip}
                aria-pressed={selectedTag === tag}
                onClick={() => setSelectedTag(tag)}
              >
                {`#${tag}`}
              </button>
            ))}
          </div>
          <motion.button
            ref={sortButtonRef}
            type="button"
            className={styles.sortButton}
            style={innerBoxStyle(
              { ...memoryLayout('memory.filter.sortButton'), y: filterTop + 6 },
              filterLayout,
            )}
            data-ui-id="memory.filter.sortButton"
            aria-label="切换排序方式"
            whileTap={presets.tap.whileTap}
            transition={presets.tap.transition}
            onClick={toggleSort}
          >
            {sortLabel}
          </motion.button>
        </div>

        <div
          ref={listRef}
          className={styles.listBox}
          style={boxStyle(listLayout, 3)}
          data-ui-id="memory.list"
          data-ui-role="list"
        >
          <motion.ul
            className={styles.list}
            aria-label="记忆列表"
            style={{ height: memories.length * memoryItemLayout(0).height }}
            variants={presets.listStagger.container}
            initial="hidden"
            animate="visible"
          >
            {memories.map((memory, index) => (
              <MemoryRow
                key={memory.id}
                memory={memory}
                index={index}
                listTop={listTop}
                presets={presets}
                onEdit={(target) => setEditor({ mode: 'edit', memory: target })}
                onRequestDelete={setPendingDelete}
              />
            ))}
          </motion.ul>
        </div>

        {!loading && memories.length === 0 ? (
          <>
            <div
              ref={emptyRef}
              className={styles.empty}
              style={boxStyle({ ...memoryLayout('memory.empty'), y: 280 + offset }, 3)}
              data-ui-id="memory.empty"
              data-ui-role="container"
            />
            <p
              ref={emptyTitleRef}
              className={styles.emptyTitle}
              style={boxStyle({ ...memoryLayout('memory.empty.title'), y: 300 + offset }, 4)}
              data-ui-id="memory.empty.title"
            >
              {EMPTY_TITLE}
            </p>
            <p
              ref={emptyHintRef}
              className={styles.emptyHint}
              style={boxStyle({ ...memoryLayout('memory.empty.hint'), y: 336 + offset }, 4)}
              data-ui-id="memory.empty.hint"
            >
              {EMPTY_HINT}
            </p>
          </>
        ) : null}

        {error !== null ? (
          <>
            <div
              ref={errorBannerRef}
              className={styles.errorBanner}
              style={boxStyle({ ...memoryLayout('memory.errorBanner'), y: listTop }, 12)}
              data-ui-id="memory.errorBanner"
              data-ui-role="container"
            />
            <p
              ref={errorTextRef}
              className={styles.errorText}
              style={boxStyle(
                { ...memoryLayout('memory.errorBanner.message'), y: listTop + 8 },
                13,
              )}
              role="alert"
              data-ui-id="memory.errorBanner.message"
            >
              {error}
            </p>
          </>
        ) : null}

        <AnimatePresence>
          {autoMemoryVisible && autoMemoryPickerOpen ? (
            <AutoMemoryModelDialog
              key="auto-memory-model"
              current={autoMemory}
              providers={providers}
              officialOptions={officialOptions}
              presets={presets}
              onCancel={() => setAutoMemoryPickerOpen(false)}
              onSave={(next) => {
                setAutoMemory(saveAutoMemorySettings(next));
                setAutoMemoryPickerOpen(false);
              }}
            />
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {editor !== null ? (
            <MemoryEditorDialog
              key={editor.mode}
              mode={editor.mode}
              initial={editor.mode === 'edit' ? editor.memory : null}
              presets={presets}
              busy={busy}
              onCancel={() => setEditor(null)}
              onSubmit={(draft) => {
                void submitEditor(draft);
              }}
            />
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {pendingDelete !== null ? (
            <DeleteConfirmDialog
              memory={pendingDelete}
              presets={presets}
              busy={busy}
              onCancel={() => setPendingDelete(null)}
              onConfirm={() => {
                void confirmDelete();
              }}
            />
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {pendingReset ? (
            <ResetConfirmDialog
              presets={presets}
              busy={busy}
              onCancel={() => setPendingReset(false)}
              onConfirm={() => {
                void confirmReset();
              }}
            />
          ) : null}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
